
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Share2, Award, TrendingUp, Users, CreditCard, 
  MessageSquare, Gift, Database, Settings
} from 'lucide-react';

const DetailedFeatures: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Comprehensive Referral Platform</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our platform offers a complete suite of tools to help you manage, track, and optimize your referral program from start to finish.
          </p>
        </div>
        
        <div className="grid gap-10">
          {/* Referral Tracking & Analytics */}
          <div>
            <h3 className="text-2xl font-bold mb-6 text-primary-blue">Referral Tracking & Analytics</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <TrendingUp className="h-8 w-8 text-primary-blue mb-2" />
                  <CardTitle>Advanced Analytics Dashboard</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Gain deep insights with comprehensive analytics showing referral sources, conversion rates, and revenue attribution. Monitor campaign performance in real-time with customizable reporting tools.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Database className="h-8 w-8 text-primary-blue mb-2" />
                  <CardTitle>Multi-Channel Tracking</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Track referrals across multiple channels including social media, email, QR codes, and direct links. Identify your most effective referral sources and optimize your strategy accordingly.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Users className="h-8 w-8 text-primary-blue mb-2" />
                  <CardTitle>Referrer Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Identify your top referrers and analyze their performance. Understand which customers are driving the most value and tailor your rewards program to incentivize continued participation.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* Reward Management */}
          <div>
            <h3 className="text-2xl font-bold mb-6 text-primary-blue">Reward Management</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Award className="h-8 w-8 text-primary-blue mb-2" />
                  <CardTitle>Flexible Reward Options</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Choose from multiple reward types including cash payments, gift cards, store credits, and custom rewards. Create tiered reward structures to incentivize higher-value referrals.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Gift className="h-8 w-8 text-primary-blue mb-2" />
                  <CardTitle>Points-Based System</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Implement a points-based system where referrers earn points for various actions that can be redeemed for rewards. Configure point values and redemption options to match your business goals.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CreditCard className="h-8 w-8 text-primary-blue mb-2" />
                  <CardTitle>Automated Payouts</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Set up automated reward disbursement via PayPal, direct deposit, or gift cards. Schedule regular payout cycles and minimize administrative overhead with our streamlined payout system.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* Customer Engagement */}
          <div>
            <h3 className="text-2xl font-bold mb-6 text-primary-blue">Customer Engagement</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Share2 className="h-8 w-8 text-primary-blue mb-2" />
                  <CardTitle>Multi-Channel Sharing</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Empower customers to share referrals via email, social media, WhatsApp, and SMS with customizable sharing templates. Create shareable QR codes for in-person referrals and print materials.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <MessageSquare className="h-8 w-8 text-primary-blue mb-2" />
                  <CardTitle>Email Notifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Keep referrers engaged with automated email notifications for referral status updates and reward payments. Customize email templates to match your brand identity.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Settings className="h-8 w-8 text-primary-blue mb-2" />
                  <CardTitle>Customizable Campaigns</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Create multiple referral campaigns with different reward structures and targeting parameters. Run limited-time promotions or special offers to boost referral activity during key periods.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DetailedFeatures;
